import SysColumn from "app/class/sys-column";
import SysUser from "app/class/sysUser";

export const DEFAULT_CURRENCY_CODE_PROPERTY = "prm.currency.default_code";
export const DEFAULT_NUMBER_FORMAT_PROPERTY = "prm.number_format.default";

export interface CurrencyRule {
    id?: string;
    code: string;
    symbol: string;
    decimalPlaces: number;
}

export interface CurrencyPreferences {
    currencies: CurrencyRule[];
    defaultCurrencyCode?: string | null;
    defaultNumberFormat?: string | null;
}

export interface NumberSeparators {
    decimal: "." | ",";
    thousand: "." | ",";
}

export const getNumberSeparators = (
    user?: SysUser | null,
    preferences?: CurrencyPreferences | null
): NumberSeparators | null => {
    const numberFormat = user?.numberFormat || preferences?.defaultNumberFormat;

    if (numberFormat === "COMMA_THOUSANDS_DOT_DECIMAL") {
        return { decimal: ".", thousand: "," };
    }

    if (numberFormat === "DOT_THOUSANDS_COMMA_DECIMAL") {
        return { decimal: ",", thousand: "." };
    }

    return null;
};

export const getCurrencyCode = (
    column?: SysColumn | null,
    preferences?: CurrencyPreferences | null,
    currencyCodeOverride?: string | null
) => currencyCodeOverride || column?.currency_code || preferences?.defaultCurrencyCode || "";

const findCurrencyRule = (
    codeOrId?: string | null,
    preferences?: CurrencyPreferences | null
) => {
    const value = codeOrId?.toString();
    if (!value) return null;

    return preferences?.currencies.find((currency) =>
        currency.id === value || currency.code.toUpperCase() === value.toUpperCase()
    ) ?? null;
};

export const getCurrencyRule = (
    code?: string | null,
    preferences?: CurrencyPreferences | null
) => {
    const rule = findCurrencyRule(code, preferences);
    if (rule) return rule;

    const defaultRule = findCurrencyRule(preferences?.defaultCurrencyCode, preferences);
    if (defaultRule) return defaultRule;

    return null;
};

export const getCurrencyDecimalPlaces = (
    column?: SysColumn | null,
    preferences?: CurrencyPreferences | null,
    currencyCodeOverride?: string | null
) => {
    if (typeof column?.currency_decimal_places === "number") {
        return column.currency_decimal_places;
    }

    return getCurrencyRule(getCurrencyCode(column, preferences, currencyCodeOverride), preferences)?.decimalPlaces ?? null;
};

export const parseCurrencyInput = (
    value: string,
    user?: SysUser | null,
    decimalPlaces?: number,
    preferences?: CurrencyPreferences | null
) => {
    const separators = getNumberSeparators(user, preferences);
    if (!separators) return null;

    const withoutSpaces = value.replace(/\s/g, "");
    const withoutThousands = withoutSpaces.split(separators.thousand).join("");
    const normalized = withoutThousands.replace(separators.decimal, ".");
    const cleaned = normalized.replace(/[^0-9.-]/g, "");

    if (!cleaned || cleaned === "-" || cleaned === "." || cleaned === "-.") {
        return null;
    }

    const [integerPart, fractionPart = ""] = cleaned.split(".");
    const limitedFraction = decimalPlaces === 0
        ? ""
        : fractionPart.slice(0, decimalPlaces ?? fractionPart.length);

    return limitedFraction ? `${integerPart}.${limitedFraction}` : integerPart;
};

export const formatCurrencyAmount = (
    value: string | null | undefined,
    decimalPlaces: number | null,
    user?: SysUser | null,
    preferences?: CurrencyPreferences | null
) => {
    if (!value) return "";
    if (decimalPlaces === null) return String(value);

    const separators = getNumberSeparators(user, preferences);
    if (!separators) return String(value);

    const normalized = String(value).replace(",", ".");
    const match = normalized.match(/^(-?)(\d+)(?:\.(\d+))?$/);

    if (!match) return String(value);

    const [, sign, rawIntegerPart, rawFractionPart = ""] = match;
    const integerPart = `${sign}${rawIntegerPart || "0"}`;
    const fractionPart = rawFractionPart.padEnd(decimalPlaces, "0").slice(0, decimalPlaces);
    const groupedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, separators.thousand);

    return decimalPlaces > 0
        ? `${groupedInteger}${separators.decimal}${fractionPart}`
        : groupedInteger;
};

export const formatCurrencyValue = (
    value: string | number | null | undefined,
    column?: SysColumn | null,
    user?: SysUser | null,
    preferences?: CurrencyPreferences | null,
    currencyCodeOverride?: string | null
) => {
    if (value === null || value === undefined || value === "") return "";

    const decimalPlaces = getCurrencyDecimalPlaces(column, preferences, currencyCodeOverride);
    const amount = formatCurrencyAmount(String(value), decimalPlaces, user, preferences);
    const rule = getCurrencyRule(getCurrencyCode(column, preferences, currencyCodeOverride), preferences);

    return rule?.symbol ? `${rule.symbol} ${amount}` : amount;
};
